import '../spoolmark-legal.css';

import { ArrowRight, ArrowUpRight, Mail } from 'lucide-react';
import type { ReactNode } from 'react';
import { WebsiteAnalyticsNotice } from '../components/WebsiteAnalyticsNotice';

export type SpoolmarkDocument =
  | 'privacy'
  | 'terms'
  | 'support'
  | 'data-deletion';

type DocumentLink = {
  key: SpoolmarkDocument;
  title: string;
  href: string;
  description: string;
};

const documentLinks: DocumentLink[] = [
  {
    key: 'privacy',
    title: 'Privacy Policy',
    href: '/spoolmark/privacy',
    description:
      'What Spoolmark stores and when information leaves your device.',
  },
  {
    key: 'terms',
    title: 'Terms of Use',
    href: '/spoolmark/terms',
    description: 'The basic rules for using Spoolmark.',
  },
  {
    key: 'support',
    title: 'Support',
    href: '/spoolmark/support',
    description: 'How to get help with the app.',
  },
  {
    key: 'data-deletion',
    title: 'Data & Deletion',
    href: '/spoolmark/data-deletion',
    description: 'How to remove data created by Spoolmark.',
  },
];

const effectiveDate = <time dateTime="2026-09-18">September 18, 2026</time>;

function DocumentNavigation({ current }: { current?: SpoolmarkDocument }) {
  return (
    <nav className="sp-document-nav" aria-label="Spoolmark documents">
      <a href="/spoolmark/docs" aria-current={!current ? 'page' : undefined}>
        All documents
      </a>
      {documentLinks.map((item) => (
        <a
          href={item.href}
          key={item.key}
          aria-current={current === item.key ? 'page' : undefined}
        >
          {item.title}
        </a>
      ))}
    </nav>
  );
}

function DocumentPage({
  current,
  eyebrow,
  title,
  introduction,
  children,
}: {
  current: SpoolmarkDocument;
  eyebrow: string;
  title: string;
  introduction: ReactNode;
  children: ReactNode;
}) {
  return (
    <main id="content" className="sp-legal-main" tabIndex={-1}>
      <header className="sp-legal-hero shell">
        <DocumentNavigation current={current} />
        <div className="sp-legal-hero-grid">
          <div>
            <p className="eyebrow">{eyebrow}</p>
            <h1>{title}</h1>
          </div>
          <div className="sp-legal-intro">
            <div>{introduction}</div>
            <p>
              Last updated{' '}
              {current === 'privacy' ? (
                <time dateTime="2026-09-18">September 18, 2026</time>
              ) : (
                effectiveDate
              )}
            </p>
          </div>
        </div>
      </header>

      <div className="sp-document-layout shell">
        <article className="sp-document-body">{children}</article>
      </div>
    </main>
  );
}

function PrivacyPolicy() {
  return (
    <DocumentPage
      current="privacy"
      eyebrow="Spoolmark"
      title="Privacy Policy"
      introduction={
        <p>
          A simple explanation of what the app stores and who can access it.
        </p>
      }
    >
      <section>
        <h2>The short version</h2>
        <p>
          Spoolmark does not require an account. Your inventory and scan and
          tag-write activity history are stored on your device, not on a
          Spoolmark server. The app has no advertising, analytics, tracking, or
          third-party crash reporting. We do not sell personal information.
        </p>
      </section>

      <section>
        <h2>Data on your device</h2>
        <p>
          The app may store filament details such as brand, material, color,
          weight, location, notes, identifiers, and dates. A successful scan or
          tag write adds normalized details to History. Saving a spool creates
          an Inventory record.
        </p>
        <p>
          Pre-release Apple test builds may include camera, NFC, OpenPrintTag
          writing, and ACR1555U support. Physical-device qualification and
          review of the bundled ACS components’ data practices and distribution
          terms are still pending; these features are not yet promised for
          public release. Camera images and recognition results are processed on
          the device and then released. The photos are not saved or uploaded by
          Spoolmark. Only details you review and save are added to Inventory and
          History.
        </p>
        <p>
          NFC reads and writes happen when you start the action. Information
          written to a physical label stays on that label and may be readable by
          another compatible device. Spoolmark never writes to an original Bambu
          Lab tag or creates an official Bambu RFID clone. It can copy selected
          spool details to a separate compatible label at your request.
        </p>
        <p>
          Optional supported readers communicate with the app locally over
          Bluetooth. Raw tag bytes are not saved to Inventory or History, but a
          copy action can place them on your device clipboard. Apple or Google
          backups may include the app’s local data.
        </p>
      </section>

      <section>
        <h2>Information we may receive</h2>
        <p>
          If you email support, we receive the message and anything you attach.
          Apple or Google may provide store reports or diagnostics under their
          own policies. They handle payment details; Spoolmark does not receive
          your card number.
        </p>
        <p>
          When you visit this website, its hosting and security providers may
          process basic request information such as your IP address, browser,
          requested page, and time of access.
        </p>
      </section>

      <section>
        <h2>Sharing</h2>
        <p>
          We do not sell data or share app inventory for advertising.
          Information is handled by Apple, Google, email, website, or backup
          providers only when needed for the service you use. Information may
          also be disclosed when required by law. Data you choose to write to a
          tag or copy to the clipboard is controlled separately from the app.
        </p>
      </section>

      <section>
        <h2>Deleting your data</h2>
        <p>
          Clear History removes the app’s History records, but not Inventory. An
          older migration file from version 0.5 may remain until all app data is
          removed. Inventory items can be archived, but archiving is not
          deletion. Versions with a Delete spool action can permanently remove
          an inventory record; its scan history remains separate.
        </p>
        <p>
          To remove all local data, clear storage for Spool Scanner on Android
          or delete the app on iPhone or iPad. Platform backups, clipboard
          content, files you exported or shared, and information already written
          to an NFC label must be removed separately. See{' '}
          <a href="/spoolmark/data-deletion">Data & Deletion</a> for the steps.
        </p>
      </section>

      <section>
        <h2>Contact</h2>
        <p>
          Spoolmark is developed by Aleksandr Nosov under the alxnsv.io name.
          The installed app is currently named <strong>Spool Scanner</strong>,
          and its Google Play listing is{' '}
          <strong>Spool Scanner (Spoolmark)</strong>.
        </p>
        <p>
          For privacy questions or to request deletion of a support email,
          contact{' '}
          <a href="mailto:hello@alxnsv.io?subject=Spoolmark%20privacy">
            hello@alxnsv.io
          </a>
          . We may update this policy when the app changes. The date above shows
          the current version.
        </p>
      </section>
      <WebsiteAnalyticsNotice />
    </DocumentPage>
  );
}

function TermsOfUse() {
  return (
    <DocumentPage
      current="terms"
      eyebrow="Spoolmark"
      title="Terms of Use"
      introduction={<p>Basic rules for using Spoolmark.</p>}
    >
      <section>
        <h2>Using Spoolmark</h2>
        <p>
          These terms apply when you download or use Spoolmark, which may appear
          as Spool Scanner on your device. You may use the app for lawful
          personal or internal business purposes, subject to these terms and the
          rules of the store where you obtained it.
        </p>
      </section>

      <section>
        <h2>Use it responsibly</h2>
        <p>
          Use only tags, readers, and devices you own or have permission to use.
          Do not break the law, interfere with the app, distribute malicious
          content, or bypass security outside Spoolmark’s documented,
          user-started workflows.
        </p>
        <p>
          Spoolmark never writes to an original Bambu Lab tag or creates an
          official, printer-recognized Bambu RFID clone. At your direction, it
          may copy selected spool details to a separate supported label.
        </p>
      </section>

      <section>
        <h2>Your information</h2>
        <p>
          You keep ownership of the inventory, notes, and other information you
          enter. It is stored locally, so you are responsible for your device,
          backups, clipboard, and data written to physical labels. Clearing app
          storage or deleting the app may permanently remove local data.
        </p>
      </section>

      <section>
        <h2>Hardware and accuracy</h2>
        <p>
          NFC, camera, and reader features depend on compatible hardware,
          permissions, platform, and app version. Review important information
          before saving it, writing a tag, or relying on it. An interrupted or
          incompatible write may damage the data on a tag.
        </p>
        <p>
          Spoolmark is an inventory tool, not a safety system. Follow the
          instructions from your filament, printer, tag, and hardware
          manufacturers.
        </p>
      </section>

      <section>
        <h2>Stores and third parties</h2>
        <p>
          Spoolmark has no in-app purchases. If an app store charges for the
          download, that store handles payment, receipts, and refunds under its
          own terms. Apple, Google, hardware manufacturers, and other third
          parties are responsible for their services.
        </p>
        <p>
          Spoolmark is not affiliated with or endorsed by Bambu Lab or ACS.
          Their names and marks belong to their owners.
        </p>
      </section>

      <section>
        <h2>Ownership, warranty, and liability</h2>
        <p>
          Spoolmark’s software, design, branding, and included content belong to
          Aleksandr Nosov or the applicable licensors. These terms let you use
          the app but do not transfer ownership of it.
        </p>
        <p>
          The app is provided “as is” and “as available.” We do not guarantee
          perfect accuracy, uninterrupted operation, or compatibility with every
          device, tag, printer, or reader. To the fullest extent permitted by
          law, we are not liable for indirect or consequential loss. Where
          liability cannot be excluded, it is limited to the amount you paid for
          the app. Rights that cannot legally be waived still apply.
        </p>
      </section>

      <section>
        <h2>Changes and contact</h2>
        <p>
          We may update these terms for future use of the app. If you do not
          agree with an updated version, stop using Spoolmark. Your permission
          to use the app ends if you materially violate these terms. If one part
          is unenforceable, the remaining parts still apply.
        </p>
        <p>
          Questions can be sent to{' '}
          <a href="mailto:hello@alxnsv.io?subject=Spoolmark%20terms">
            hello@alxnsv.io
          </a>
          .
        </p>
      </section>
    </DocumentPage>
  );
}

function SupportPage() {
  return (
    <DocumentPage
      current="support"
      eyebrow="Spoolmark"
      title="Support"
      introduction={
        <>
          <p>Support goes directly to the person building Spoolmark.</p>
          <a
            className="sp-document-primary-action"
            href="mailto:hello@alxnsv.io?subject=Spoolmark%20support"
          >
            <Mail size={18} aria-hidden="true" /> Email support
            <ArrowUpRight size={16} aria-hidden="true" />
          </a>
        </>
      }
    >
      <section>
        <h2>Contact</h2>
        <p>
          Email{' '}
          <a href="mailto:hello@alxnsv.io?subject=Spoolmark%20support">
            hello@alxnsv.io
          </a>
          . We aim to respond within three business days, including questions
          about the paid Google Play download.
        </p>
        <p>
          Please include your device model, operating-system version, app
          version, the action you tried, and the exact error message.
          Screenshots can help, but crop private notes and identifiers first. Do
          not send passwords, encryption keys, access credentials, or raw tag
          dumps.
        </p>
      </section>

      <section>
        <h2>Before clearing or reinstalling</h2>
        <p>
          Your inventory and history are stored on your device. Clearing app
          storage or reinstalling the app can permanently erase them. If
          Spoolmark reports a storage error, contact support before doing
          either.
        </p>
      </section>

      <section>
        <h2>NFC and supported devices</h2>
        <p>
          On Android, the phone must support NFC. Original Bambu Lab tags also
          require MIFARE Classic support. Compatible Spool Scanner and
          OpenPrintTag labels can be used on supported hardware.
        </p>
        <p>
          Pre-release Apple test builds may include camera, NFC, OpenPrintTag
          writing, and ACR1555U support. Physical-device qualification and
          review of the bundled ACS components’ data practices and distribution
          terms are still pending; these features are not yet promised for
          public release. Built-in Apple NFC cannot authenticate original Bambu
          Lab tags.
        </p>
        <p>
          Spoolmark never writes to an original Bambu Lab tag. At your
          direction, it can copy selected details to a separate compatible
          label. This is not an official or printer-recognized Bambu RFID clone.
        </p>
      </section>

      <section>
        <h2>Purchases</h2>
        <p>
          Spoolmark has no in-app purchases. Download charges, receipts, and
          refunds are handled by Google Play or the App Store.
        </p>
      </section>
    </DocumentPage>
  );
}

function DataDeletionPage() {
  return (
    <DocumentPage
      current="data-deletion"
      eyebrow="Spoolmark"
      title="Data & Deletion"
      introduction={<p>How to remove data created by Spoolmark.</p>}
    >
      <section>
        <h2>No account to delete</h2>
        <p>
          Spoolmark does not create an account or keep your inventory on a
          Spoolmark server. Your inventory, history, preferences, and
          identifiers are stored on your device.
        </p>
      </section>

      <section>
        <h2>Delete History</h2>
        <p>
          Open Scan History, choose <strong>Clear History</strong>, and confirm.
          This deletes all History events in the app, including older events not
          shown in the recent list. It does not delete Inventory.
        </p>
        <p>
          On some installations upgraded from version 0.5, an old hidden history
          file may remain until all app data is removed. The app does not show
          or import that file after History is cleared.
        </p>
      </section>

      <section>
        <h2>Delete Inventory and all app data</h2>
        <p>
          Deletion controls depend on your installed version. Where available,
          use <strong>Delete spool</strong> and confirm to permanently remove an
          inventory record and its QR and NFC associations. This does not erase
          the physical label or the separate scan history. Archiving a spool
          only hides it and can be undone.
        </p>
        <p>
          If your version does not offer Delete spool, or you want to remove all
          local records:
        </p>
        <ul>
          <li>
            On Android, clear storage for <strong>Spool Scanner</strong> in
            system settings or uninstall the app.
          </li>
          <li>
            On iPhone or iPad, choose <strong>Delete App</strong>. Offload App
            may keep the app’s data.
          </li>
        </ul>
        <p>These actions cannot be undone by Spoolmark.</p>
      </section>

      <section>
        <h2>Backups, tags, and clipboard</h2>
        <p>
          Apple or Google backups may retain or restore a copy of app data. Use
          your platform’s backup settings to manage those copies.
        </p>
        <p>
          JSON backups, CSV exports, and PDF labels you save or share are
          separate files. Remove those files and any shared copies separately;
          deleting a spool or the app does not erase them.
        </p>
        <p>
          Deleting the app does not erase information already written to a
          physical NFC label. Erase or overwrite the label separately where it
          permits this. Clipboard content is also controlled by your device and
          should be cleared or replaced separately.
        </p>
        <p>
          Camera images, recognition results, and raw tag bytes are temporary
          and are not saved as files by Spoolmark. A successful scan or tag
          write stores normalized details in History; saving to Inventory
          creates an additional Inventory record. The raw tag dump is not stored
          in either.
        </p>
      </section>

      <section>
        <h2>Store, website, and support data</h2>
        <p>
          Google Play and the App Store manage their own account, download,
          payment, and diagnostic records. Website providers may retain basic
          request and security logs under their own policies.
        </p>
        <p>
          To request deletion of a support conversation, email{' '}
          <a href="mailto:hello@alxnsv.io?subject=Delete%20Spoolmark%20support%20data">
            hello@alxnsv.io
          </a>{' '}
          from the same address. Some backup copies may remain temporarily until
          the email provider’s retention period ends.
        </p>
      </section>
    </DocumentPage>
  );
}

export function SpoolmarkDocsPage() {
  return (
    <main id="content" className="sp-legal-main" tabIndex={-1}>
      <header className="sp-legal-hero sp-docs-hero shell">
        <DocumentNavigation />
        <div className="sp-legal-hero-grid">
          <div>
            <p className="eyebrow">Spoolmark</p>
            <h1>Documents</h1>
          </div>
          <div className="sp-legal-intro">
            <div>
              <p>Privacy, terms, support, and deletion information.</p>
            </div>
            <p>Last updated {effectiveDate}</p>
          </div>
        </div>
      </header>

      <section className="sp-doc-index shell" aria-label="Document index">
        {documentLinks.map((item) => (
          <article key={item.key}>
            <div>
              <h2>{item.title}</h2>
              <p>{item.description}</p>
            </div>
            <a href={item.href} aria-label={`Read ${item.title}`}>
              Read <ArrowRight size={17} aria-hidden="true" />
            </a>
          </article>
        ))}
      </section>

      <section className="sp-doc-contact shell">
        <div>
          <p className="eyebrow">Need help?</p>
          <h2>Contact the developer.</h2>
        </div>
        <div>
          <p>
            Questions go directly to the independent developer of Spoolmark.
          </p>
          <a href="mailto:hello@alxnsv.io?subject=Spoolmark%20question">
            hello@alxnsv.io <ArrowUpRight size={16} aria-hidden="true" />
          </a>
        </div>
      </section>
    </main>
  );
}

export function SpoolmarkLegalPage({
  document,
}: {
  document: SpoolmarkDocument;
}) {
  switch (document) {
    case 'privacy':
      return <PrivacyPolicy />;
    case 'terms':
      return <TermsOfUse />;
    case 'support':
      return <SupportPage />;
    case 'data-deletion':
      return <DataDeletionPage />;
  }
}
